/**
 * שלד הנתיבים הציבוריים — TASKS 6.2 · DESIGN.md 6.5.
 *
 * ⭐ **Route Layout** של react-router: `<Route element={<PublicShell />}>`
 *    עוטף את כל נתיבי הציבור, ו-`<Outlet/>` מרנדר את הנתיב שהתאים.
 *    ⛔ **פאנל הניהול (`/admin/*`) ⛔ אינו עובר דרך הרכיב הזה** — הוא נשאר
 *    כנתיב אחות נפרד ב-`main.tsx`, בלי `Layout`/`AnimatePresence` (DESIGN.md
 *    6.5: "אפס אנימציית מעבר בין מסכי הפאנל").
 *
 * ⭐ **Header קבוע מעל ה-`AnimatePresence`** — הוא חלק מ-`Layout`, שעוטף את
 *    ה-`AnimatePresence` מבחוץ ולכן ⛔ לעולם לא נכנס/יוצא בעצמו במעבר עמוד.
 *
 * ⭐ **reset גלילה מיידי** — `window.scrollTo(0, 0)` ברגע שה-pathname
 *    משתנה, ⛔ לא `behavior: 'smooth'` (DESIGN.md 6.5: "reset לראש העמוד
 *    מיידית בין יציאה לכניסה").
 *
 * ⭐ **סנכרון `document.title`** — נמצא 21.08.2026: ניווט פנימי (`<Link>`)
 *    השאיר את כותרת טאב הדפדפן של העמוד הקודם, כי שום קוד לא קרא ל-
 *    `document.title` (רק הזרקת השרת בטעינה ראשונה, `middleware/
 *    seoMetaInjection.ts`, שלא רץ שוב במעבר SPA). לעמודים סטטיים - כאן,
 *    לפי `content/pageTitles.ts`. לדף מוצר (כותרת דינמית) - `ProductPage.tsx`
 *    עצמו קובע אחרי טעינת הנתונים, ⛔ לא כאן.
 */
import { cloneElement, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import { useLocation, useOutlet } from 'react-router-dom';
import { COMPANY_NAME } from '../content/businessInfo.js';
import { STATIC_PAGE_TITLES } from '../content/pageTitles.js';
import { Layout } from './Layout.js';
import { PageTransition } from './PageTransition.js';

export function PublicShell() {
  const location = useLocation();
  // ⚠️ ⛔ **בכוונה `useOutlet()`, לא `<Outlet/>` כילד JSX.** נתפס בפועל:
  //    עטיפת `<Outlet/>` כילד-JSX בתוך `AnimatePresence` (גם עם `key` על
  //    עוטף מעליו, גם בלי) גרמה לרכיב המנותב **להירכב מחדש בשקט** זמן קצר
  //    אחרי הכניסה — כל `useState` מקומי (כולל טופס מלא, `RequestPage`)
  //    התאפס בלי שינוי URL, בלי שגיאת console. ⭐ הפתרון המתועד של
  //    react-router + motion: `useOutlet()` מחזיר את האלמנט המנותב ישירות,
  //    וה-`key` מוצמד **עליו עצמו** דרך `cloneElement` — לא על עוטף מעליו.
  const element = useOutlet();

  useEffect(() => {
    window.scrollTo(0, 0);
    // ⭐ עמוד מוצר (כותרת דינמית) לא מופיע ב-`STATIC_PAGE_TITLES` בכוונה -
    // נופל כאן לברירת מחדל (שם החברה בלבד), ו-`ProductPage.tsx` דורס אותה
    // מיד אחרי טעינת הנתונים.
    document.title = STATIC_PAGE_TITLES[location.pathname] ?? COMPANY_NAME;
  }, [location.pathname]);

  return (
    <Layout>
      <AnimatePresence mode="wait" initial={false}>
        {element && (
          <PageTransition key={location.pathname}>
            {cloneElement(element, { key: location.pathname })}
          </PageTransition>
        )}
      </AnimatePresence>
    </Layout>
  );
}
